import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { css, Text, tokens } from "@balabalabom/ui";
import { Header } from "./components/Header";
import { StoreRedirect } from "./components/StoreRedirect";
import { Home } from "./pages/Home";
import { About } from "./pages/About";
import { Event } from "./pages/Event";
import { Events } from "./pages/Events";
import { Story } from "./pages/Story";
import { MiniBookInstructionBackground } from "./pages/MiniBookInstructionBackground";
import { MiniBookCreate } from "./pages/MiniBookCreate";
import { MiniBookProject } from "./pages/MiniBookProject";
import { isMiniBookEnabled } from "./lib/featureFlags";
import {
  getStoreBaseUrl,
  SHOP_HOME_URL,
  shouldRedirectHomeToStore,
} from "./lib/storeUrls";
import "./styles/mobile-first.css";

function isStoreHandoffPath(pathname: string): boolean {
  return (
    pathname === "/shop" ||
    pathname.startsWith("/shop/") ||
    (pathname === "/" && shouldRedirectHomeToStore())
  );
}

function App() {
  const storeUrl = getStoreBaseUrl();

  return (
    <BrowserRouter>
      <AppShell storeUrl={storeUrl} />
    </BrowserRouter>
  );
}

function AppShell({ storeUrl }: { storeUrl: string | null }) {
  const { pathname } = useLocation();
  const handoffToStore = Boolean(storeUrl) && isStoreHandoffPath(pathname);

  return (
    <div
      style={css({
        minHeight: "100vh",
        backgroundColor: handoffToStore ? "#fdfbf7" : "#f5f0e8",
      })}
    >
      {handoffToStore ? null : <Header />}

      <Routes>
        <Route
          path="/"
          element={
            shouldRedirectHomeToStore() ? (
              <StoreRedirect href={SHOP_HOME_URL} />
            ) : (
              <Home />
            )
          }
        />
        <Route path="/stories" element={<Home />} />
        <Route
          path="/home"
          element={
            <Navigate
              to={shouldRedirectHomeToStore() ? "/stories" : "/"}
              replace
            />
          }
        />
        <Route path="/about" element={<About />} />
        <Route path="/events" element={<Events />} />
        <Route path="/event/:eventId" element={<Event />} />
        <Route path="/story/:storyId" element={<Story />} />
        <Route
          path="/shop"
          element={
            storeUrl ? (
              <StoreRedirect preservePath />
            ) : (
              <Navigate to="/stories" replace />
            )
          }
        />
        <Route
          path="/shop/*"
          element={
            storeUrl ? (
              <StoreRedirect preservePath />
            ) : (
              <Navigate to="/stories" replace />
            )
          }
        />
        <Route
          path="/minibook"
          element={
            isMiniBookEnabled ? (
              <MiniBookCreate />
            ) : (
              <Navigate to="/stories" replace />
            )
          }
        />
        <Route
          path="/minibook/instructionBackground"
          element={
            isMiniBookEnabled ? (
              <MiniBookInstructionBackground />
            ) : (
              <Navigate to="/stories" replace />
            )
          }
        />
        <Route
          path="/minibook/project/:projectId"
          element={
            isMiniBookEnabled ? (
              <MiniBookProject />
            ) : (
              <Navigate to="/stories" replace />
            )
          }
        />
      </Routes>

      {handoffToStore ? null : (
        <footer
          style={css({
            backgroundColor: tokens.colors.background.tertiary,
            padding: "1rem",
            textAlign: "center",
            marginTop: "2rem",
          })}
        >
          <Text
            variant="body"
            style={css({
              fontSize: "0.9rem",
              color: tokens.colors.neutral[600],
            })}
          >
            © 2025 <strong>Balabalabom</strong>. All Rights Reserved.
            <br />
            {storeUrl && (
              <a
                href={storeUrl}
                style={css({
                  color: tokens.colors.brand.primary,
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                })}
              >
                Shop at Balabalabom Store
              </a>
            )}
          </Text>
        </footer>
      )}
    </div>
  );
}

export default App;
